import { Component } from '@theme/component';
import { sectionRenderer, morphSection } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { connectMobilePurchaseLayout } from './zaivik-mobile-purchase.js?v=20261029-3';

/** Gallery and pack navigation. Native theme blocks own cart and checkout. */
class ZaivikGheeProduct extends Component {
  #listeners;
  #request;
  #requestTimeout;
  #observer;
  #scrollFrame;
  #imageIndex = 0;
  #cartPending = false;
  #pointer;
  #mobilePurchaseCleanup;

  connectedCallback() { super.connectedCallback(); this.#connect(); }
  updatedCallback() { super.updatedCallback(); this.#connect(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#mobilePurchaseCleanup?.();
    this.#request?.abort();
    clearTimeout(this.#requestTimeout);
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#scrollFrame);
  }

  #connect() {
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#imageIndex = 0;
    const stage = this.querySelector('.ghee-gallery__stage');
    stage?.addEventListener('pointerdown', (event) => { this.#pointer = { x: event.clientX, y: event.clientY }; }, { signal });
    stage?.addEventListener('pointerup', (event) => {
      if (!this.#pointer) return;
      const dx = event.clientX - this.#pointer.x;
      const dy = event.clientY - this.#pointer.y;
      this.#pointer = undefined;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.5) this.#showImage(this.#imageIndex + (dx < 0 ? 1 : -1));
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (!(event.target instanceof Element) || !event.target.closest('.ghee-gallery')) return;
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      this.#showImage(this.#imageIndex + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, { signal });
    const dialog = this.querySelector('dialog');
    dialog?.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const b = dialog.getBoundingClientRect();
      if (event.clientX < b.left || event.clientX > b.right || event.clientY < b.top || event.clientY > b.bottom) dialog.close();
    }, { signal });
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, () => this.#syncQuantity(), { signal });
    this.addEventListener('input', () => this.#syncQuantity(), { signal });
    this.addEventListener('change', () => this.#syncQuantity(), { signal });
    this.addEventListener(StandardEvents.cartError, () => {
      const status = this.querySelector('[data-purchase-status]');
      if (status) status.textContent = this.dataset.cartError || '';
    }, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, async (event) => {
      if (!event.promise) return;
      this.#cartPending = true;
      this.setAttribute('aria-busy', 'true');
      const status = this.querySelector('[data-purchase-status]');
      if (status) status.textContent = '';
      try {
        const result = await event.promise;
        if (status && !result.detail?.didError) {
          status.textContent = `${this.dataset.cartSuccess || ''} `;
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.textContent = this.dataset.viewCart;
          status.append(link);
        }
      } catch {
        if (status) status.textContent = this.dataset.cartError || '';
      }
      finally { this.#cartPending = false; this.removeAttribute('aria-busy'); }
    }, { signal });
    const purchase = this.querySelector('[data-purchase]');
    const sticky = this.querySelector('[data-sticky]');
    if (purchase && sticky) {
      this.#mobilePurchaseCleanup = connectMobilePurchaseLayout(sticky, signal);
      const footer = document.querySelector('footer, .shopify-section-group-footer-group');
      const update = () => {
        const footerVisible = footer && footer.getBoundingClientRect().top < innerHeight;
        sticky.hidden = purchase.getBoundingClientRect().bottom > 0 || Boolean(footerVisible);
      };
      this.#observer = new IntersectionObserver(update);
      this.#observer.observe(purchase);
      // A fast swipe can skip the observed area without changing its intersection ratio.
      window.addEventListener('scroll', () => {
        cancelAnimationFrame(this.#scrollFrame);
        this.#scrollFrame = requestAnimationFrame(update);
      }, { signal, passive: true, capture: true });
      window.addEventListener('resize', update, { signal, passive: true });
      update();
    }
    this.#syncQuantity();
    this.#updateImageCount();
  }

  /** @param {MouseEvent} event */
  selectImage(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target instanceof Element ? event.target.closest('[data-gallery-image]') : null;
    if (!link) return;
    event.preventDefault();
    this.#showImage([...this.querySelectorAll('[data-gallery-image]')].indexOf(link));
  }
  previousImage() { this.#showImage(this.#imageIndex - 1); }
  nextImage() { this.#showImage(this.#imageIndex + 1); }
  #updateImageCount() {
    const count = this.querySelector('[data-gallery-count]');
    if (count) count.textContent = `${this.#imageIndex + 1} / ${this.querySelectorAll('[data-gallery-image]').length}`;
  }
  #showImage(index, focus = false) {
    const links = [...this.querySelectorAll('[data-gallery-image]')];
    if (!links.length) return;
    this.#imageIndex = (index + links.length) % links.length;
    const link = links[this.#imageIndex];
    const image = this.querySelector('.ghee-gallery__image');
    if (!(image instanceof HTMLImageElement) || !(link instanceof HTMLAnchorElement)) return;
    image.removeAttribute('srcset');
    image.src = link.href;
    image.alt = link.dataset.alt || '';
    const caption = this.querySelector('[data-gallery-caption]');
    if (caption) caption.textContent = link.dataset.caption || '';
    for (const item of links) item.removeAttribute('aria-current');
    link.setAttribute('aria-current', 'true');
    const rail = link.parentElement;
    rail?.scrollTo({ left: link.offsetLeft - rail.offsetLeft - rail.clientWidth / 2 + link.clientWidth / 2, behavior: this.#motion() });
    if (focus) link.focus({ preventScroll: true });
    this.#updateImageCount();
  }
  openZoom() {
    const dialog = this.querySelector('dialog');
    const image = this.querySelector('.ghee-gallery__image');
    const zoomImage = dialog?.querySelector('img');
    if (!(dialog instanceof HTMLDialogElement) || !(image instanceof HTMLImageElement) || !zoomImage) return;
    zoomImage.src = image.currentSrc;
    zoomImage.alt = image.alt;
    dialog.showModal();
  }
  closeZoom() { this.querySelector('dialog')?.close(); }

  async share() {
    const url = new URL(this.dataset.productUrl, location.origin);
    url.searchParams.set('variant', this.dataset.variantId);
    const status = this.querySelector('[data-share-status]');
    try {
      if (navigator.share) await navigator.share({ title: this.querySelector('h1')?.textContent, url: url.toString() });
      else {
        await navigator.clipboard.writeText(url.toString());
        if (status) status.textContent = this.dataset.linkCopied || '';
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      const input = this.querySelector('[data-share-url]');
      if (input instanceof HTMLInputElement) {
        input.hidden = false;
        input.value = url.toString();
        input.focus();
        input.select();
      }
    }
  }

  /** Render a fresh native form so availability, quantity rules and accelerated checkout agree.
   * @param {MouseEvent} event
   */
  async selectPack(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    const link = event.target instanceof Element ? event.target.closest('[data-pack-link]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;
    event.preventDefault();
    if (this.#cartPending || this.hasAttribute('aria-busy') || link.dataset.variantId === this.dataset.variantId) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', link.dataset.variantId);
    url.searchParams.set('view', this.dataset.templateView || 'a2-bilona');
    const desiredVariant = link.dataset.variantId;
    const sectionId = this.dataset.sectionId;
    const quantity = this.querySelector('product-form-component input[name="quantity"]')?.value || '1';
    this.#request?.abort();
    this.#request = new AbortController();
    const controller = this.#request;
    const { signal } = controller;
    clearTimeout(this.#requestTimeout);
    this.#requestTimeout = setTimeout(() => controller.abort('timeout'), 15000);
    this.setAttribute('aria-busy', 'true');
    const status = this.querySelector('[data-purchase-status]');
    if (status) status.textContent = this.dataset.loadingLabel || '';
    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url, signal);
      if (signal.aborted || !this.isConnected) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      if (parsed.querySelector('zaivik-ghee-product')?.dataset.variantId !== desiredVariant) throw new Error('Invalid pack response');
      await morphSection(sectionId, html, { mode: 'full', injectStylesheet: true });
      const live = document.getElementById(`shopify-section-${sectionId}`)?.querySelector('zaivik-ghee-product');
      if (!live || live.dataset.variantId !== desiredVariant) throw new Error('Pack render did not complete');
      live.removeAttribute('aria-busy');
      const input = live.querySelector('product-form-component input[name="quantity"]');
      if (input instanceof HTMLInputElement) {
        const min = Number(input.min) || 1;
        const step = Number(input.step) || 1;
        const max = input.max ? Number(input.max) : Infinity;
        input.value = String(Math.min(max, min + Math.max(0, Math.ceil((Number(quantity) - min) / step)) * step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      history.replaceState({}, '', url);
      window.Shopify?.PaymentButton?.init?.();
      live.querySelector(`[data-variant-id="${desiredVariant}"][data-pack-link]`)?.focus({ preventScroll: true });
    } catch {
      if (signal.aborted && signal.reason !== 'timeout') return;
      this.removeAttribute('aria-busy');
      if (status) {
        status.replaceChildren(document.createTextNode(`${this.dataset.packError || ''} `));
        const retry = document.createElement('a');
        retry.href = url.toString();
        retry.textContent = link.querySelector('.ghee-pack__name')?.textContent.trim() || '';
        status.append(retry);
        status.focus();
      }
    } finally {
      clearTimeout(this.#requestTimeout);
    }
  }
  stickyAdd() {
    if (!this.hasAttribute('aria-busy')) this.querySelector('product-form-component button[name="add"]')?.click();
  }
  #syncQuantity() {
    const quantity = this.querySelector('product-form-component input[name="quantity"]');
    const stickyQuantity = this.querySelector('.ghee-sticky__checkout input[name="quantity"]');
    if (quantity instanceof HTMLInputElement && stickyQuantity instanceof HTMLInputElement) stickyQuantity.value = quantity.value;
    const quantityLabel = this.querySelector('[data-sticky-quantity]');
    if (quantity instanceof HTMLInputElement && quantityLabel) quantityLabel.textContent = `× ${quantity.value}`;
  }
  #motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
}

/** Scroll-snap process cards: keyboard, touch and button navigation, without autoplay. */
class GheeCarousel extends Component {
  #listeners;
  #observer;
  #frame;
  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }
  #setup() {
    this.#listeners?.abort();
    this.#observer?.disconnect();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    const track = this.querySelector('.ghee-carousel__track');
    if (!track) return;
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#update());
    }, { signal, passive: true });
    track.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.#move(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.#observer = new ResizeObserver(() => this.#update());
    this.#observer.observe(track);
    this.#update();
  }
  previous() { this.#move(-1); }
  next() { this.#move(1); }
  #move(direction) {
    const track = this.querySelector('.ghee-carousel__track');
    const slides = [...this.querySelectorAll('[data-slide]')];
    if (!track || !slides.length) return;
    const step = slides[1] ? slides[1].offsetLeft - slides[0].offsetLeft : track.clientWidth;
    track.scrollBy({ left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
  #index() {
    const track = this.querySelector('.ghee-carousel__track');
    const slides = [...this.querySelectorAll('[data-slide]')];
    if (!track || !slides.length) return 0;
    if (Math.abs(track.scrollLeft) + track.clientWidth >= track.scrollWidth - 5) return slides.length - 1;
    const bounds = track.getBoundingClientRect();
    const edge = getComputedStyle(track).direction === 'rtl' ? 'right' : 'left';
    return slides.reduce((best, slide, i) => Math.abs(slide.getBoundingClientRect()[edge] - bounds[edge]) < Math.abs(slides[best].getBoundingClientRect()[edge] - bounds[edge]) ? i : best, 0);
  }
  #update() {
    const index = this.#index();
    const count = this.querySelectorAll('[data-slide]').length;
    const status = this.querySelector('[data-carousel-status]');
    if (status) status.textContent = `${String(index + 1).padStart(2, '0')} / ${String(count).padStart(2, '0')}`;
    const buttons = this.querySelectorAll('.ghee-carousel__controls button');
    if (buttons[0]) buttons[0].disabled = index === 0;
    if (buttons[1]) buttons[1].disabled = index >= count - 1;
  }
}
if (!customElements.get('zaivik-ghee-product')) customElements.define('zaivik-ghee-product', ZaivikGheeProduct);
if (!customElements.get('ghee-carousel')) customElements.define('ghee-carousel', GheeCarousel);
